'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Services', href: '/services' },
      { label: 'Portfolio', href: '/portfolio' },
      { label: 'AI Hub', href: '/ai-automation-hub' }
    ],
    resources: [
      { label: 'Case Studies', href: '/portfolio' },
      { label: 'Contact', href: '/contact' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Cookie Policy', href: '/cookies' }
    ],
    contact: [
      { icon: 'EnvelopeIcon', text: 'info@consultintech.com' },
      { icon: 'MapPinIcon', text: 'London, W8 4LZ, UK' }
    ]
  };

  return (
    <footer className="bg-authority text-authority-foreground">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div className="space-y-4">
            <Link href="/homepage" className="flex items-center space-x-2">
              <div className="relative w-10 h-10">
                <svg
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-full h-full"
                >
                  <rect width="40" height="40" rx="8" fill="url(#footer-logo-gradient)" />
                  <path
                    d="M12 20C12 15.5817 15.5817 12 20 12C24.4183 12 28 15.5817 28 20C28 24.4183 24.4183 28 20 28"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle cx="20" cy="20" r="3" fill="white" />
                  <defs>
                    <linearGradient id="footer-logo-gradient" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#1e3a5f" />
                      <stop offset="1" stopColor="#2d5a87" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <span className="text-xl font-display font-bold">CIS Solutions</span>
            </Link>
            <p className="text-sm text-authority-foreground/80 leading-relaxed">
              Transforming businesses through strategic AI automation and innovative marketing solutions. Your trusted partner in digital transformation.
            </p>
            <div className="mt-4 space-y-1 text-xs text-authority-foreground/70">
              <p><strong>Company:</strong> CIT SMART SOLUTIONS LTD</p>
              <p><strong>Reg No:</strong> 17015313</p>
              <p><strong>Address:</strong> First Floor Office, 3 Hornton Place, London, W8 4LZ, UNITED KINGDOM</p>
            </div>
          </div>

          <div>
            <h3 className="font-display font-semibold text-lg mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="text-sm text-authority-foreground/80 hover:text-accent transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-display font-semibold text-lg mb-4">Resources</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="text-sm text-authority-foreground/80 hover:text-accent transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-display font-semibold text-lg mb-4">Contact</h3>
            <ul className="space-y-3">
              {footerLinks.contact.map((item, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon name={item.icon as any} size={16} className="text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-authority-foreground/80">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-authority-foreground/20">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-sm text-authority-foreground/80">
              {isHydrated && currentYear ? `© ${currentYear}` : '©'} CIS Solutions. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;